/**
 * What a client keeps of the rows no open sheet holds (judge P r30 P3).
 *
 * `SyncHttpBackend` keeps one `RowCellCache` per row for the whole client:
 * every open sheet holding the row shares it (`RowCellCache.fetchBindings`
 * counts the holders). Before this, a row stayed in the registry until the
 * client was disposed, so a long session kept every sheet it ever opened —
 * about 7.2 MB per distinct 100-row sheet, linear, with no plateau.
 *
 * Now a row whose last holder releases it goes to an LRU bounded by cell
 * count (`IDLE_ROW_CELLS`). Past the bound, the least recently used idle row
 * is evicted: dropped from the registry, its cells and fetch coverage
 * cleared (`RowCellCache.evictCells`), so a reopen builds a fresh cache and
 * fetches again — never a stale read. A row a sheet binds again (or any
 * `rowHandle` read) leaves the LRU or moves to its recent end. A row that
 * cannot be dropped unnoticed (`RowCellCache.isEvictable`: a listener, a
 * fetch in flight, an outstanding write revision) is skipped; a row no sheet
 * ever bound (a standalone reader's) is never idle, so never evicted.
 */

import type { Option } from "@tento-lona/sheets";
import type { RowCellCache } from "./row-cell-cache.ts";

/** Cells the idle rows of one client may hold before the least recently used one is evicted. */
export const IDLE_ROW_CELLS = 50_000;

export class RowCacheRetention {
  readonly #maxIdleCells: number;
  readonly #drop: (key: string, rh: RowCellCache) => void;
  /** Every tracked cache → its registry key (re-keyed on promote). */
  readonly #keys = new Map<RowCellCache, string>();
  /** Idle rows, least recently used first → the cells each held when it went idle. */
  readonly #idle = new Map<RowCellCache, number>();
  #idleCells = 0;
  #evicted = 0;
  /**
   * Releases that may skip the full pass: set when a pass visited every idle
   * row and still ended over the bound (every row it kept could not be
   * dropped), to the number of rows it visited. Such a row turns droppable
   * only when its listener, fetch or write revision goes, which the
   * retention does not hear of; until the budget runs out a release tries
   * the next idle row in least-recently-used order (`#cursor`, so an older
   * droppable row goes before a newer one, as a full pass would take it)
   * and then its own row, and the next full pass retries the rest. So n
   * releases cost O(n) evictability reads (at most two each in skip mode),
   * and a row that turned droppable goes within `idle.rows` releases.
   */
  #skips = 0;
  /** A live walk of `#idle` in least-recently-used order, one row per skipped release; restarts when done. */
  #cursor: Option<Iterator<[RowCellCache, number]>> = null;
  /** The row the walk stopped at (it could not be dropped then): the next skipped release tries it first. */
  #stoppedAt: Option<RowCellCache> = null;

  /**
   * An LRU of at most `maxIdleCells` idle cells; `drop` removes an evicted
   * row from the backend's registry (the retention clears its cells).
   */
  constructor(maxIdleCells: number, drop: (key: string, rh: RowCellCache) => void) {
    this.#maxIdleCells = maxIdleCells;
    this.#drop = drop;
  }

  /** Rows evicted so far (instrumentation). */
  get evicted(): number {
    return this.#evicted;
  }

  /** Idle rows held now, and their cells (instrumentation). */
  get idle(): { readonly rows: number; readonly cells: number } {
    return { rows: this.#idle.size, cells: this.#idleCells };
  }

  /** Follow `rh`, registered under `key`, from now on. */
  track(key: string, rh: RowCellCache): void {
    this.#keys.set(rh, key);
    rh.fetchBindings.observe({
      held: () => this.#leave(rh),
      released: () => this.#release(rh),
    });
  }

  /**
   * `rh` was read (`rowHandle`): an idle row moves to the recent end, and its
   * cell count is taken again: the store under an idle row can still change
   * (a sync push, an external edit), so the count recorded at release may be
   * stale. The bound is applied at the next release, not on a read.
   */
  touch(rh: RowCellCache): void {
    const cells = this.#idle.get(rh);
    if (cells == null) return;
    this.#idle.delete(rh);
    const now = rh.cellCount;
    this.#idle.set(rh, now);
    this.#idleCells += now - cells;
  }

  /** `rh` is registered under `key` now (a promote re-key). */
  rekey(rh: RowCellCache, key: string): void {
    if (this.#keys.has(rh)) this.#keys.set(rh, key);
  }

  /** Stop following `rh` (the row was deleted). */
  forget(rh: RowCellCache): void {
    this.#leave(rh);
    this.#keys.delete(rh);
    rh.fetchBindings.observe(null);
  }

  /** Stop following every row (the client's dispose). */
  clear(): void {
    for (const rh of this.#keys.keys()) rh.fetchBindings.observe(null);
    this.#keys.clear();
    this.#idle.clear();
    this.#idleCells = 0;
    this.#skips = 0;
    this.#cursor = null;
    this.#stoppedAt = null;
  }

  #leave(rh: RowCellCache): void {
    const cells = this.#idle.get(rh);
    if (cells == null) return;
    this.#idle.delete(rh);
    this.#idleCells -= cells;
  }

  #release(rh: RowCellCache): void {
    this.#leave(rh);
    const cells = rh.cellCount;
    this.#idle.set(rh, cells);
    this.#idleCells += cells;
    this.#trim(rh);
  }

  /**
   * Evict least recently used idle rows until the idle cells fit (rows that
   * cannot be dropped are skipped). `released` is the row whose release
   * asked: while the skip budget lasts it alone is tried (`#skips`).
   */
  #trim(released: RowCellCache): void {
    if (this.#idleCells <= this.#maxIdleCells) return;
    if (this.#skips > 0) {
      this.#skips--;
      // First the next least recently used idle row (Map iteration is live:
      // rows evicted behind the cursor are skipped, rows released since are
      // reached in their turn), so an older row that turned droppable goes
      // before a newer one; then, still over the bound, the released row.
      // The walk goes on while it frees rows and stops at the first it
      // cannot drop: each row is evicted once, so this stays O(1) amortized.
      let restarted = false;
      const stopped = this.#stoppedAt;
      this.#stoppedAt = null;
      if (stopped != null && stopped !== released) {
        const cells = this.#idle.get(stopped);
        if (cells != null) {
          if (!stopped.isEvictable) {
            this.#stoppedAt = stopped;
            return this.#trimReleased(released);
          }
          this.#evict(stopped, cells);
        }
      }
      while (this.#idleCells > this.#maxIdleCells) {
        let next = this.#cursor?.next();
        if (next == null || next.done) {
          if (restarted) break;
          restarted = true;
          this.#cursor = this.#idle.entries();
          next = this.#cursor.next();
          if (next.done) break;
        }
        const [rh] = next.value;
        if (rh === released) continue;
        const cells = this.#idle.get(rh);
        if (cells == null) continue;
        if (!rh.isEvictable) {
          this.#stoppedAt = rh;
          break;
        }
        this.#evict(rh, cells);
      }
      this.#trimReleased(released);
      return;
    }
    this.#cursor = null;
    this.#stoppedAt = null;
    let visited = 0;
    // The live map: an evicted row is deleted behind the cursor, which Map iteration allows.
    for (const [rh, cells] of this.#idle) {
      if (this.#idleCells <= this.#maxIdleCells) return;
      visited++;
      if (!rh.isEvictable) continue;
      this.#evict(rh, cells);
    }
    if (this.#idleCells > this.#maxIdleCells) this.#skips = visited;
  }

  /** Still over the bound after the walk: try the released row itself. */
  #trimReleased(released: RowCellCache): void {
    if (this.#idleCells <= this.#maxIdleCells) return;
    const cells = this.#idle.get(released);
    if (cells != null && released.isEvictable) this.#evict(released, cells);
  }

  #evict(rh: RowCellCache, cells: number): void {
    this.#idle.delete(rh);
    this.#idleCells -= cells;
    const key = this.#keys.get(rh);
    this.#keys.delete(rh);
    rh.fetchBindings.observe(null);
    if (key != null) this.#drop(key, rh);
    rh.evictCells();
    this.#evicted++;
  }
}
